import { proxy } from 'valtio';

/**
 * Offering data from nivaas API
 */
export interface NivaasOffering {
  __typename: 'RoomOffering';
  id: string;
  name: string;
  slug: string;
  capacity: number;
  inventory: number;
  description: string;
  pricing: Array<{
    unitPrice: {
      amount: number;
      currency: string;
    };
    gst: number;
    applicableFromDate: string;
    applicableOnDates?: string[] | null;
    applicableUntilDate?: string | null;
  } | null>;
}

/**
 * Global offerings state - populated from root loader
 */
export const offeringsState = proxy<{
  offerings: NivaasOffering[];
  initialized: boolean;
}>({
  offerings: [],
  initialized: false,
});

/**
 * Initialize offerings state from loader data
 */
export function initializeOfferings(offerings: NivaasOffering[]) {
  offeringsState.offerings = offerings;
  offeringsState.initialized = true;
}

/**
 * Get offering by slug
 */
export function getOfferingBySlug(slug: string): NivaasOffering | undefined {
  return offeringsState.offerings.find((o) => o.slug === slug);
}

/**
 * Get all available slugs from offerings
 */
export function getAvailableSlugs(): string[] {
  return offeringsState.offerings.map((o) => o.slug);
}

/**
 * Build a capacity map from offerings (slug -> capacity)
 */
export function getCapacityMap(): Map<string, number> {
  const map = new Map<string, number>();
  for (const offering of offeringsState.offerings) {
    map.set(offering.slug, offering.capacity);
  }
  return map;
}

/**
 * Get capacity for a specific slug
 */
export function getOfferingCapacity(slug: string): number | undefined {
  const offering = getOfferingBySlug(slug);
  return offering?.capacity;
}

/**
 * Get unit price from an offering (per-person price)
 */
export function getOfferingDisplayPrice(
  offering: NivaasOffering | null | undefined,
): { amount: number; currency: string; gst: number } | null {
  const price = offering?.pricing[0]?.unitPrice;
  if (!price) return null;

  return {
    amount: price.amount,
    currency: price.currency,
    gst: offering?.pricing[0]?.gst || 0,
  };
}

/**
 * Get full room price from an offering (unit_price * capacity)
 * This is what customers actually pay per night for a room.
 * Use this for display purposes to avoid confusion.
 */
export function getOfferingFullRoomPrice(
  offering: NivaasOffering | null | undefined,
): { amount: number; currency: string; gst: number; capacity: number } | null {
  const price = offering?.pricing[0]?.unitPrice;
  if (!price) return null;

  const capacity = offering?.capacity ?? 1;
  return {
    amount: price.amount * capacity,
    currency: price.currency,
    gst: offering?.pricing[0]?.gst || 0,
    capacity,
  };
}

/**
 * Get full room price for a slug
 * Returns the price in INR (unit_price * capacity), or undefined if not found
 */
export function getOfferingPrice(slug: string): number | undefined {
  const offering = getOfferingBySlug(slug);
  const price = getOfferingFullRoomPrice(offering);
  return price?.amount;
}

/**
 * Get the current price for an offering based on a specific date
 * Returns the most specific applicable pricing strategy
 */
export function getOfferingPriceForDate(
  offering: NivaasOffering | null | undefined,
  date?: string,
): { amount: number; currency: string; gst: number } | null {
  if (!offering?.pricing || offering.pricing.length === 0) {
    return null;
  }

  const targetDate = date ? new Date(date) : new Date();

  // Filter pricing strategies applicable to the target date
  const applicablePricing = offering.pricing
    .filter((p): p is NonNullable<typeof p> => p !== null)
    .filter((p) => {
      const fromDate = new Date(p.applicableFromDate);
      const untilDate = p.applicableUntilDate ? new Date(p.applicableUntilDate) : null;

      // Check if date is within the range
      if (targetDate < fromDate) return false;
      if (untilDate && targetDate > untilDate) return false;

      // If specific dates are defined, check if target date is in that list
      if (p.applicableOnDates && p.applicableOnDates.length > 0) {
        const targetDateStr = targetDate.toISOString().split('T')[0];
        return p.applicableOnDates.includes(targetDateStr);
      }

      return true;
    });

  if (applicablePricing.length === 0) {
    return null;
  }

  // Prefer specific date pricing over range pricing
  const specificDatePricing = applicablePricing.find(
    (p) => p.applicableOnDates && p.applicableOnDates.length > 0,
  );

  const selectedPricing = specificDatePricing || applicablePricing[0];

  return {
    amount: selectedPricing.unitPrice.amount,
    currency: selectedPricing.unitPrice.currency,
    gst: selectedPricing.gst,
  };
}

/**
 * Format price for display
 */
export function formatPrice(price: { amount: number; currency: string } | null): string {
  if (!price) return 'Contact for pricing';

  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: price.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return formatter.format(price.amount);
}

/**
 * Price computation result
 */
export interface PriceComputation {
  /** Total price before GST (rooms + package) */
  total: number;
  /** GST amount (rooms + package) */
  totalGst: number;
  /** Total price including GST */
  totalWithGst: number;
  /** Number of nights */
  nights: number;
  /** GST percentage for accommodation (e.g., 5 for 5%) */
  gstPercentage: number;

  /** Breakdown: accommodation cost before GST */
  accommodationTotal: number;
  /** Breakdown: accommodation GST */
  accommodationGst: number;

  /** Breakdown: package supplementary items cost before GST */
  packageTotal: number;
  /** Breakdown: package supplementary items GST */
  packageGst: number;
  /** Breakdown: average GST percentage for package items (for display) */
  packageGstPercentage: number;
  /** Breakdown: individual package supplementary items for display */
  packageItems: Array<{
    name: string;
    quantity: number;
    unitPrice: number;
    total: number;
    gst: number;
    gstPercentage: number;
  }>;
}

/**
 * Calculate price based on offerings data (legacy - for fallback)
 * Formula: total = unit_price * capacity * nights * quantity
 *
 * This aligns with nivaas backend which multiplies by capacity:
 * - Customers pay for the full room capacity
 * - unit_price is per-person, but we always charge for max capacity
 *
 * GST comes from offerings
 */
export function calculatePriceFromOfferings(
  startDate: string,
  endDate: string,
  rooms: Array<[string, number]>,
): PriceComputation {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const nights = Math.max(
    0,
    Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)),
  );

  let total = 0;
  let gstPercentage = 0;
  for (const [slug, quantity] of rooms) {
    const offering = getOfferingBySlug(slug);
    if (!offering) continue;

    const priceInfo = getOfferingDisplayPrice(offering);
    if (!priceInfo) continue;

    // Formula: unit_price * capacity * nights * quantity
    // Aligns with nivaas which multiplies items by capacity
    const unitPrice = priceInfo.amount;
    const capacity = offering.capacity;
    total += unitPrice * capacity * nights * quantity;

    // Track GST percentage (use the first non-zero one)
    if (priceInfo.gst > 0) {
      gstPercentage = priceInfo.gst; // GST is already a percentage (e.g., 5 for 5%)
    }
  }

  // If no GST found from offerings, default to 0
  const gstFraction = gstPercentage / 100;
  const totalGst = total * gstFraction;

  return {
    total,
    totalGst,
    totalWithGst: total + totalGst,
    nights,
    gstPercentage,
    // Breakdown - initially just accommodation
    accommodationTotal: total,
    accommodationGst: totalGst,
    packageTotal: 0,
    packageGst: 0,
    packageGstPercentage: 0,
    packageItems: [],
  };
}

/**
 * Rate card entry type (imported from package types)
 */
interface RateCardEntry {
  offering: { id: string; name?: string };
  buyerType: string;
  occupancy: number;
  unitPrice: { amount: number; currency: string };
  gst: number;
}

/**
 * Calculate price based on rate card data from a package.
 * 
 * For occupancy-based pricing (supportsOccupancyPricing = true):
 *   - Look up rate card by occupancy (1 or 2)
 *   - Multiply per-person price by occupancy
 * 
 * For flat pricing (supportsOccupancyPricing = false):
 *   - Look up rate card by offering capacity
 *   - Multiply per-person price by offering capacity
 *
 * @param startDate - Start date of stay
 * @param endDate - End date of stay
 * @param rooms - Array of [slug, quantity] pairs
 * @param rateCard - Rate card entries from the selected package
 * @param occupancy - The occupancy for occupancy-based pricing (1 for single, 2 for double)
 * @param buyerType - PUBLIC or AGENT
 * @param supportsOccupancyPricing - Whether workspace uses occupancy-based pricing
 */
export function calculatePriceFromRateCard(
  startDate: string,
  endDate: string,
  rooms: Array<[string, number]>,
  rateCard: RateCardEntry[] | undefined,
  occupancy: number,
  buyerType: string = 'PUBLIC',
  supportsOccupancyPricing: boolean = false,
): PriceComputation {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const nights = Math.max(
    0,
    Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)),
  );

  // If no rate card, fall back to offerings-based pricing
  if (!rateCard || rateCard.length === 0) {
    return calculatePriceFromOfferings(startDate, endDate, rooms);
  }

  let total = 0;
  let gstPercentage = 0;

  for (const [slug, quantity] of rooms) {
    const offering = getOfferingBySlug(slug);
    if (!offering) continue;

    const offeringCapacity = offering.capacity;
    
    // Determine which occupancy to look up and which multiplier to use
    // For occupancy-based: use passed occupancy (1 or 2)
    // For flat pricing: use offering capacity
    const lookupOccupancy = supportsOccupancyPricing ? occupancy : offeringCapacity;
    const priceMultiplier = supportsOccupancyPricing ? occupancy : offeringCapacity;

    // Find matching rate card entry for this offering, buyer type, and occupancy
    const entry = rateCard.find(
      (e) =>
        e.offering.id === offering.id &&
        e.buyerType === buyerType &&
        e.occupancy === lookupOccupancy,
    );

    if (entry) {
      // Rate card price is per-person, multiply by the appropriate multiplier
      // Formula: unit_price * multiplier * nights * quantity
      const unitPrice = entry.unitPrice.amount;
      total += unitPrice * priceMultiplier * nights * quantity;

      if (entry.gst > 0) {
        gstPercentage = entry.gst;
      }
    } else {
      // No rate card entry found, fall back to offering base pricing
      const priceInfo = getOfferingDisplayPrice(offering);
      if (priceInfo) {
        // Use offering's capacity for fallback (traditional pricing)
        total += priceInfo.amount * offeringCapacity * nights * quantity;
        if (priceInfo.gst > 0) {
          gstPercentage = priceInfo.gst;
        }
      }
    }
  }

  const gstFraction = gstPercentage / 100;
  const totalGst = total * gstFraction;

  return {
    total,
    totalGst,
    totalWithGst: total + totalGst,
    nights,
    gstPercentage,
    accommodationTotal: total,
    accommodationGst: totalGst,
    packageTotal: 0,
    packageGst: 0,
    packageGstPercentage: 0,
    packageItems: [],
  };
}
